"use strict";

import angular from "angular";
import _ from "lodash";

import {
    attachCompanyIndicators,
    findIndicatorByAssociated
} from "../../../../../../utils/dre-group-indicators";
import { setDreLevelsByDepth } from "../../../../../../utils/dre-group-levels";

class FinancialDreService {
    constructor() {}

    /**
     * @param {Object} options
     * @param {Boolean} options.isGroup Recalcula `level` pela profundidade real da arvore.
     * @param {String} options.groupId Desempata o indicador do grupo e ancora a quebra por empresa.
     * @param {Array} options.companies Empresas-membro, para rotular as linhas filhas.
     */
    seriesAndIndicatorsGroup(
        series,
        indicators,
        { isGroup = false, groupId = null, companies = [] } = {}
    ) {
        series.forEach((serie) => {
            const father = this.findFather(series, serie._id);
            if (father) {
                serie.father = father._id;

                if (!father.childrens) father.childrens = [];

                const hasChildren = father.childrens.find(
                    (children) => children._id == serie._id
                );
                if (!hasChildren) father.childrens.push(serie);
            }
        });

        // verificar associated para não listar contas personalizadas no nível 0
        const masterSeries = series.filter((s) => !s.father && s.associated);
        this.setIndicators(masterSeries, indicators, groupId);

        let list = this.getOrderedSeriesList(masterSeries);

        if (isGroup) {
            list = attachCompanyIndicators({
                list,
                indicators,
                groupId,
                companies
            });
            setDreLevelsByDepth(list);
        }

        return list;
    }

    /**
     * A quebra por empresa, aplicada a uma lista que ja veio PRONTA.
     *
     * `seriesAndIndicatorsGroup` monta a ordem e depois pendura as empresas. Quando a ordem vem do
     * DOCUMENTO da Customizacao, so a segunda metade interessa — mas ela nao pode ficar de fora, ou
     * a DRE de grupo perde a quebra por empresa ao ser customizada.
     */
    applyGroupBreakdown(
        list,
        indicators,
        { isGroup = false, groupId = null, companies = [] } = {}
    ) {
        if (!isGroup) return list;

        const comEmpresas = attachCompanyIndicators({
            list,
            indicators,
            groupId,
            companies
        });
        setDreLevelsByDepth(comEmpresas);
        return comEmpresas;
    }

    getSeriesGroupForReport(series) {
        series.forEach((serie) => {
            const father = this.findFather(series, serie._id);
            if (father) {
                serie.father = father._id;

                if (!father.childrens) father.childrens = [];

                const hasChildren = father.childrens.find(
                    (children) => children._id == serie._id
                );
                if (!hasChildren) father.childrens.push(serie);
            }
        });

        const masterSeries = series.filter((s) => !s.father);

        const list = this.getOrderedSeriesList(masterSeries);

        return list;
    }

    getOrderedSeriesList(masterSeries) {
        let list = [];
        masterSeries.forEach((master) => {
            list.push(master);

            this.addChildrens(master.childrens, list);
        });

        return list;
    }

    addChildrens(childrens, list) {
        if (!childrens) return;

        childrens.forEach((children) => {
            list.push(children);

            const childrens = children.childrens;
            this.addChildrens(childrens, list);
        });
    }

    setIndicators(masterSeries, indicators, groupId = null) {
        // indicators nature
        indicators.forEach((indicator) => {
            if (
                !_.get(indicator, "metadata.natureOfAccount") &&
                _.get(indicator, "metadata.dre")
            ) {
                const hasSignalOnName = indicator.name.indexOf("(") > -1;
                // Adiciona sinal se não houver
                if (!hasSignalOnName) indicator.name = `(=) ${indicator.name}`;
            }
        });

        // Associated
        const indicatorOrder = {
            kNPRazOz9pnnXyJhHTT76: {
                index: 2,
                aboveSerieAssociated: "sIAeUyPOywdwitsfrFSyM"
            }, // Receita Líquida, acima de Custos
            "666708ed447704a04cce8b54": {
                index: 4,
                aboveSerieAssociated: "c7wXoAxkBU0T7hKAceffZ"
            }, // Lucro Bruto, acima de Despesas e Outras Receitas
            michd53d3d7898818e81c45e: {
                index: 5,
                aboveSerieAssociated: "c7wXoAxkBU0T7hKAceffZ"
            }, // Peso da Margem de Contribuição, acima de Despesas e Outras Receitas
            "349708ed447704a04cce8b54": {
                index: 7,
                aboveSerieAssociated: "ExENclm6zqfgxENM8eCwQ"
            } // Resultado Financeiro Operacional, acima de Atividade de Investimento
        };

        // Adiciona indicadores após series pre-definidas
        for (const indicatorId in indicatorOrder) {
            const indicator = findIndicatorByAssociated(
                indicators,
                indicatorId,
                groupId
            );
            const aboveSerieIndex = masterSeries.findIndex((s) =>
                s.associated?.includes(
                    indicatorOrder[indicatorId].aboveSerieAssociated
                )
            );
            if (indicator)
                masterSeries.splice(
                    aboveSerieIndex > -1
                        ? aboveSerieIndex
                        : indicatorOrder[indicatorId].index,
                    0,
                    indicator
                );
        }

        // Variação de Caixa é sempre o último
        const lastIndicator = findIndicatorByAssociated(
            indicators,
            "variacaosdnqZE06YcDcxuSu",
            groupId
        ); // Variação de Caixa
        if (lastIndicator) masterSeries.push(lastIndicator);
    }

    findFather(series, serieId) {
        return series.find((serie) => {
            if (!serie.compound) return false;
            return serie.compound.includes(serieId);
        });
    }
}

FinancialDreService.$inject = [];

export { FinancialDreService };

angular.module("nucont.shared").service("FinancialDreService", FinancialDreService);
